import {
  type AvailableFont,
  hasLoadedPreviewFont,
  loadFont,
  loadPreviewFont,
} from '@/fonts/fonts';
import { useCallback, useMemo, useState } from 'react';
import { List } from 'react-window';

export function FontPicker({
  font,
  fontList,
  onSelectFont,
}: {
  font: AvailableFont;
  fontList: AvailableFont[];
  onSelectFont: (family: AvailableFont) => void;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  // 1. Fast local array filtering
  const filteredFonts = useMemo(() => {
    const q = search.toLowerCase().trim();
    if (!q) return fontList;
    return fontList.filter(f => f.family.toLowerCase().includes(q));
  }, [fontList, search]);

  // 2. Individual row renderer
  const Row = ({
    index,
    style,
  }: {
    index: number;
    style: React.CSSProperties;
  }) => {
    const font = filteredFonts[index]!;
    const [hasLoaded, setHasLoaded] = useState(
      () => font.type === 'standard' || hasLoadedPreviewFont(font.family),
    );

    // Trigger lazy loading when the item renders inside the virtual view
    const onMouseOver = useCallback(() => {
      if (font.type !== 'google') return;
      loadPreviewFont(font.family);
      setHasLoaded(true);
    }, [font]);

    const onClick = useCallback(() => {
      setOpen(false);
      setSearch('');
      onSelectFont(font);
      if (font.type !== 'google') return;
      loadFont(font.family);
    }, [font]);

    return (
      <button
        className="flex cursor-pointer items-center justify-between overflow-hidden border-b border-gray-200 px-2 text-sm text-nowrap hover:bg-gray-100"
        style={{
          ...style,
          fontFamily: hasLoaded ? `'${font.family}', sans-serif` : undefined,
        }}
        onMouseOver={onMouseOver}
        onClick={onClick}
      >
        {font.family}
      </button>
    );
  };

  return (
    <div className="relative w-48">
      <input
        type="text"
        placeholder={font.family}
        value={search}
        onChange={e => setSearch(e.target.value)}
        style={{ width: '100%', padding: 8, boxSizing: 'border-box' }}
        onFocus={() => setOpen(true)}
      />
      {open && (
        <>
          <div
            className="fixed inset-0 z-10"
            onClick={() => {
              setOpen(false);
              setSearch('');
            }}
          />
          <div className="absolute top-full left-0 z-100 max-h-100 w-96 overflow-y-auto bg-white">
            <List
              rowComponent={Row}
              rowHeight={32}
              rowCount={filteredFonts.length}
              //@ts-expect-error the types are bad
              rowProps={{}}
            />
          </div>
        </>
      )}
    </div>
  );
}
